import type { Messages } from "../fr";

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export const emails: Messages["emails"] = {
  layout: {
    greeting: (firstName: string | null) => (firstName ? `Hi ${firstName},` : "Hi,"),
    signature: "The Hune team",
    legalLine: (entity: { brand: string; name: string; address: string }) =>
      `${entity.brand}, published by ${entity.name} SAS, ${entity.address}.`,
    labeled: (label: string, value: string) => `${label}: ${value}`,
    labeledBlock: (label: string, value: string) => `${label}:\n${value}`,
    fallbackLink: "Button not working? Copy this address into your browser:",
    settings: "Settings",
  },
  passwordReset: {
    subject: "Reset your Hune password",
    preheader: "The link is valid for one hour.",
    title: "Choose a new password",
    intro: "You asked to change the password of your Hune account. This link is valid for one hour.",
    action: "Choose a new password",
    ignore: "If you did not make this request, ignore this message: your password stays the same.",
    why: "You are receiving this email because a password reset was requested for your account.",
  },
  invitation: {
    subject: "Your Hune account is ready",
    preheader: "Choose your password to access Hune.",
    title: "Your account is ready",
    intro: "A Hune account has just been created for you. All that’s left is to choose your password: the link is valid for one hour.",
    action: "Choose my password",
    expired: {
      before: "After that, use “Forgot your password?” on the ",
      link: "login page",
      after: ".",
    },
    expiredText: (loginUrl: string) => `After that, use “Forgot your password?” on the login page: ${loginUrl}`,
    why: "You are receiving this email because a Hune administrator created an account at this address.",
  },
  welcome: {
    started: {
      subject: "Your Hune agent is at work",
      title: "Your agent is at work",
      preheader: "The first pass takes about ten minutes. You have nothing to do.",
      opening: "The agent has already started its first pass. Allow about ten minutes: you have nothing to do.",
      stepsIntro: "Meanwhile:",
      action: "Follow the agent",
    },
    notStarted: {
      subject: "Your Hune workspace is ready",
      title: "Your workspace is ready",
      preheader: "Run the first analysis from the Overview.",
      opening:
        "The first analysis could not start on its own. Run it from the Overview: then allow about ten minutes, with nothing else to do.",
      stepsIntro: "Once it runs:",
      action: "Run the first analysis",
    },
    lead: (title: string, opening: string) => `${title}. ${opening}`,
    steps: {
      readSite: (site: string | null) => `It reads your offer’s website${site ? ` (${site})` : ""} and works out your target.`,
      search: "It searches the web for organizations showing a buying signal, dated and sourced.",
      pipeline: "Leads arrive in your pipeline, each with its signal and the contacts to reach out to.",
    },
    targetingText: "Targeting off? Correct it in “My offers”: the next pass takes it into account.",
    targeting: {
      before: "Targeting off? Correct it in ",
      link: "“My offers”",
      after: ": the next pass takes it into account.",
    },
    recap:
      "After that, monitoring runs on its own. You will get a summary once a week at most, and only if the agent has found something new.",
    why: "You are receiving this email because you just created your Hune workspace; there will be no other email like it.",
    unsubscribeText: (url: string) => `To stop receiving the weekly summary: ${url}`,
    unsubscribeLink: "Stop receiving the weekly summary",
  },
  digest: {
    counts: (newLeads: number, reactivated: number) => {
      const parts: string[] = [];
      if (newLeads > 0) parts.push(plural(newLeads, "new lead", "new leads"));
      if (reactivated > 0) {
        parts.push(newLeads > 0 ? `${reactivated} reactivated` : plural(reactivated, "reactivated lead", "reactivated leads"));
      }
      return parts.join(" and ");
    },
    offerCounts: (newLeads: number, reactivated: number) => {
      const parts: string[] = [];
      if (newLeads > 0) parts.push(`${newLeads} new`);
      if (reactivated > 0) parts.push(`${reactivated} reactivated`);
      return parts.join(", ");
    },
    subject: (totals: string, sinceLastDigest: boolean, severalOffers: boolean) =>
      `${totals} ${sinceLastDigest ? "since the last summary" : "in the last seven days"}${severalOffers ? " across your offers" : ""}`,
    title: (totals: string, sinceLastDigest: boolean) =>
      `${totals} ${sinceLastDigest ? "since the last summary" : "in the last seven days"}`,
    introText: (sinceLastDigest: boolean, totals: string, offer: { single: string } | { count: number }) => {
      const period = sinceLastDigest ? "Since the last summary" : "Over the last seven days";
      return "single" in offer
        ? `${period}, the agent found something new for ${offer.single}: ${totals}.`
        : `${period}, the agent found something new on ${offer.count} of your offers: ${totals} in all.`;
    },
    introHtml: (sinceLastDigest: boolean, offer: { single: string } | { count: number }) => {
      const period = sinceLastDigest ? "Since the last summary" : "Over the last seven days";
      return "single" in offer
        ? `${period}, the agent found something new for ${offer.single}. Here are the leads to look at first.`
        : `${period}, the agent found something new on ${offer.count} of your offers. Here are the leads to look at first for each one.`;
    },
    reactivatedNote: "A reactivated lead is one you had dismissed that is showing a new signal.",
    quiet: (sinceLastDigest: boolean, offers: string[]) =>
      `Nothing new ${sinceLastDigest ? "since then" : "in the last seven days"} on ${offers.join(", ")}: monitoring continues.`,
    reactivated: "reactivated",
    seeLeads: (count: number) => (count === 1 ? "See the lead" : `See the ${count} leads`),
    seeOfferLeads: (count: number) => (count === 1 ? "See this offer’s lead" : `See this offer’s ${count} leads`),
    openHune: "Open Hune",
    others: (count: number) => `And ${plural(count, "other lead", "other leads")} in your pipeline.`,
    nextPass: (day: string) => `Next pass: ${day}`,
    yourOffer: "your offer",
    why: "You are receiving this summary because the agent found something new on your offers. It comes once a week at most, and never when there is nothing.",
    unsubscribeText: (url: string, settingsUrl: string) =>
      `Stop receiving it: ${url} (or from your settings: ${settingsUrl}).`,
    unsubscribeLink: "Stop receiving it",
    preheader: (totals: string, offerCount: number) =>
      offerCount > 1
        ? `${totals} across ${offerCount} offers, with their signal and source.`
        : `${totals}, with their signal and source.`,
  },
};
